/**
 * @fileoverview Charts Page Controller
 * Entry point for Chart Mode - full-screen multi-signal analysis workspace.
 * 
 * URL Format: #/charts?signals=NET_LIQUIDITY,VIX_TERM_SPREAD&range=90d&mode=normalized
 * 
 * @module pages/charts
 */

import { el } from '../shared/components/simpleRender.js';
import { ChartMode, initChartMode, cleanupChartMode, getChartModeState } from '../charts/components/ChartMode.js';

/**
 * Default chart configuration
 */
const DEFAULTS = {
  range: '90d',
  mode: 'normalized',
  events: true,
};

/**
 * Parse URL search params for chart state
 * @returns {Object} Parsed chart configuration
 */
function parseChartParams() {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf('?');
  
  if (queryIndex === -1) {
    return {
      signals: [],
      range: DEFAULTS.range,
      mode: DEFAULTS.mode,
      events: DEFAULTS.events,
    };
  }
  
  const queryString = hash.slice(queryIndex + 1);
  const params = new URLSearchParams(queryString);
  
  // Parse signals (comma-separated)
  const signalsParam = params.get('signals') || '';
  const signals = signalsParam
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter(s => s.length > 0)
    .slice(0, 4); // Max 4 signals
  
  return {
    signals,
    range: params.get('range') || DEFAULTS.range,
    mode: params.get('mode') || DEFAULTS.mode,
    events: params.get('events') !== 'off',
  };
}

/**
 * Update URL with current chart state (without triggering navigation)
 * @param {Object} state - Current chart state
 */
export function updateChartURL(state) {
  const params = new URLSearchParams();
  
  if (state.signals && state.signals.length > 0) {
    params.set('signals', state.signals.join(','));
  }
  if (state.range && state.range !== DEFAULTS.range) {
    params.set('range', state.range);
  }
  if (state.mode && state.mode !== DEFAULTS.mode) {
    params.set('mode', state.mode);
  }
  if (state.events === false) {
    params.set('events', 'off');
  }
  
  const queryString = params.toString();
  const newHash = queryString ? `#/charts?${queryString}` : '#/charts';
  
  // Update URL without triggering hashchange
  if (window.location.hash !== newHash) {
    history.replaceState(null, '', newHash);
  }
}

/**
 * Navigate to Chart Mode with specific signals
 * @param {string[]} signals - Array of signal IDs
 * @param {Object} options - Optional configuration
 */
export function navigateToChartMode(signals = [], options = {}) {
  const params = new URLSearchParams();
  
  if (signals.length > 0) {
    params.set('signals', signals.join(','));
  }
  if (options.range) {
    params.set('range', options.range);
  }
  if (options.mode) {
    params.set('mode', options.mode);
  }
  if (options.events === false) {
    params.set('events', 'off');
  }
  
  const queryString = params.toString();
  window.location.hash = queryString ? `/charts?${queryString}` : '/charts';
}

/**
 * Renders the Charts page
 * @returns {HTMLElement} Charts page element
 */
export function renderChartsPage() {
  const config = parseChartParams();
  
  console.log('[Charts] Rendering with config:', config);
  
  // Create chart mode with parsed configuration
  const chartModeEl = ChartMode(config);
  
  // Wrap in page container
  const page = el('div', { className: 'charts-page' }, [
    chartModeEl,
  ]);
  
  // Initialize after render
  setTimeout(() => {
    initChartMode(config);
  }, 50);
  
  return page;
}

/**
 * Cleanup function for charts page
 */
export function cleanupChartsPage() {
  cleanupChartMode();
}

/**
 * Get current chart state (for sharing, etc.)
 * @returns {Object} Current chart configuration
 */
export function getChartsPageState() {
  return getChartModeState();
}
